import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  FaArrowLeft,
  FaSpinner,
  FaSave,
  FaCheck,
  FaTimes,
  FaUsers,
  FaCheckCircle,
  FaEye,
  FaEyeSlash,
  FaMicrophone,
  FaRobot,
} from "react-icons/fa";

interface Assignment {
  id: number;
  classTestId: number;
  classTestTitle: string;
  level: string;
  chapterRange: string;
  batchName: string;
  totalStudents: number;
  submitted: number;
  graded: number;
  published: number;
}

interface Attempt {
  id: number;
  studentName: string;
  status: string;
  submittedAt: string | null;
  lesenScore: number | null;
  lesenMaxScore: number | null;
  horenScore: number | null;
  horenMaxScore: number | null;
  schreibenScore: number | null;
  schreibenMaxScore: number | null;
  sprechenScore: number | null;
  sprechenMaxScore: number | null;
  totalScore: number | null;
  maxScore: number | null;
  percentage: number | null;
  passed: boolean | null;
  resultsPublished: boolean;
}

interface Question {
  id: number;
  module: string;
  questionNumber: number;
  questionType: string;
  questionText: string;
  correctAnswer: string | null;
  points: number;
  options: string[] | null;
  contextText: string | null;
  contextImageUrl: string | null;
  blankCount: number | null;
}

const parseJsonArray = (s: string | null): string[] => {
  if (!s) return [];
  try {
    const p = JSON.parse(s);
    if (Array.isArray(p)) return p.map(String);
  } catch {}
  return [s];
};

interface Answer {
  id: number;
  questionId: number;
  studentAnswer: string | null;
  isCorrect: boolean | null;
  pointsEarned: number | null;
  maxPoints: number | null;
  feedback: string | null;
  gradedBy: string | null;
  gradedAt: string | null;
}

const isManual = (type: string) =>
  ["TEXT_INPUT", "ESSAY", "EMAIL", "LETTER"].includes(type);

const MODULE_ICONS: Record<string, string> = {
  LESEN: "📖",
  HOREN: "🎧",
  SCHREIBEN: "✏️",
  SPRECHEN: "🎤",
};

const LEVEL_COLORS: Record<string, string> = {
  A1: "bg-green-100 text-green-800",
  A2: "bg-blue-100 text-blue-800",
  B1: "bg-purple-100 text-purple-800",
  B2: "bg-orange-100 text-orange-800",
};

interface Props {
  assignmentId: number;
  onBack: () => void;
  apiBase?: string;
}

const AdminClassTestGrade: React.FC<Props> = ({ assignmentId, onBack, apiBase }) => {
  const base = apiBase ?? `${process.env.REACT_APP_API_BASE_URL}/api/admin/class-tests`;
  const [assignment, setAssignment] = useState<Assignment | null>(null);
  const [attempts, setAttempts] = useState<Attempt[]>([]);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [selectedAttemptId, setSelectedAttemptId] = useState<number | null>(null);
  const [answers, setAnswers] = useState<Answer[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingAnswers, setLoadingAnswers] = useState(false);
  const initialSelectionDone = useRef(false);

  // Per-answer grading state: answerId → { points, feedback }
  const [gradeInputs, setGradeInputs] = useState<Record<number, { points: string; feedback: string }>>({});
  const [savingAnswer, setSavingAnswer] = useState<number | null>(null);

  // Sprechen
  const [sprechenScore, setSprechenScore] = useState("");
  const [sprechenMaxScore, setSprechenMaxScore] = useState("20");
  const [savingSprechen, setSavingSprechen] = useState(false);

  // AI evaluation
  const [evaluatingAI, setEvaluatingAI] = useState(false);

  const [publishing, setPublishing] = useState(false);
  const [publishingAll, setPublishingAll] = useState(false);
  const [msg, setMsg] = useState<{ type: "ok" | "err"; text: string } | null>(null);

  const flash = (type: "ok" | "err", text: string) => {
    setMsg({ type, text });
    setTimeout(() => setMsg(null), 3000);
  };

  const loadAttempts = useCallback(async () => {
    try {
      const [aRes, assRes] = await Promise.all([
        fetch(`${base}/assignments/${assignmentId}/attempts`, { credentials: "include" }),
        fetch(`${base}/assignments/${assignmentId}`, { credentials: "include" }),
      ]);
      if (aRes.ok) {
        const data: Attempt[] = await aRes.json();
        setAttempts(data);
        if (data.length > 0 && !initialSelectionDone.current) {
          setSelectedAttemptId(data[0].id);
          initialSelectionDone.current = true;
        }
      }
      if (assRes.ok) {
        const d = await assRes.json();
        setAssignment(d);
        // Load questions
        const qRes = await fetch(`${base}/${d.classTestId}/questions`, { credentials: "include" });
        if (qRes.ok) setQuestions(await qRes.json());
      }
    } finally {
      setLoading(false);
    }
  }, [assignmentId]);

  useEffect(() => { loadAttempts(); }, [loadAttempts]);

  useEffect(() => {
    if (!selectedAttemptId) return;
    setLoadingAnswers(true);
    fetch(`${base}/attempts/${selectedAttemptId}/answers`, { credentials: "include" })
      .then((r) => r.json())
      .then((data: Answer[]) => {
        setAnswers(data);
        const inputs: Record<number, { points: string; feedback: string }> = {};
        data.forEach((a) => {
          inputs[a.id] = {
            points: a.pointsEarned?.toString() ?? "",
            feedback: a.feedback ?? "",
          };
        });
        setGradeInputs(inputs);
      })
      .catch(console.error)
      .finally(() => setLoadingAnswers(false));

    // Pre-fill sprechen score from attempt; fallback max from question config
    const attempt = attempts.find((a) => a.id === selectedAttemptId);
    if (attempt) {
      setSprechenScore(attempt.sprechenScore?.toString() ?? "");
      const computedMax = questions
        .filter((q) => q.module === "SPRECHEN")
        .reduce((sum, q) => sum + (q.points ?? 0), 0);
      const maxVal = attempt.sprechenMaxScore || computedMax || 0;
      setSprechenMaxScore(maxVal.toString());
    }
  }, [selectedAttemptId, attempts, questions]);

  const handleEvaluateAI = async () => {
    if (!selectedAttemptId) return;
    if (!window.confirm("Use AI to evaluate all Schreiben answers for this student?")) return;
    setEvaluatingAI(true);
    try {
      const res = await fetch(`${base}/attempts/${selectedAttemptId}/evaluate-schreiben`, {
        method: "POST",
        credentials: "include",
      });
      if (res.ok) {
        const d = await res.json();
        flash("ok", `AI evaluated ${d.evaluated} answer(s)`);
        // Reload answers and attempt scores
        setLoadingAnswers(true);
        const [aRes] = await Promise.all([
          fetch(`${base}/attempts/${selectedAttemptId}/answers`, { credentials: "include" }),
        ]);
        if (aRes.ok) {
          const data: Answer[] = await aRes.json();
          setAnswers(data);
          const inputs: Record<number, { points: string; feedback: string }> = {};
          data.forEach((a) => {
            inputs[a.id] = { points: a.pointsEarned?.toString() ?? "", feedback: a.feedback ?? "" };
          });
          setGradeInputs(inputs);
        }
        setLoadingAnswers(false);
        loadAttempts();
      } else {
        flash("err", "AI evaluation failed");
      }
    } finally {
      setEvaluatingAI(false);
    }
  };

  const handleGradeAnswer = async (answerId: number) => {
    const input = gradeInputs[answerId];
    if (!input) return;
    setSavingAnswer(answerId);
    try {
      const res = await fetch(`${base}/answers/grade`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          answerId,
          pointsEarned: Number(input.points),
          feedback: input.feedback || null,
        }),
      });
      if (res.ok) {
        flash("ok", "Saved");
        const updated: Answer = await res.json();
        setAnswers((prev) => prev.map((a) => (a.id === answerId ? updated : a)));
        loadAttempts();
      } else {
        flash("err", "Failed to save grade");
      }
    } finally {
      setSavingAnswer(null);
    }
  };

  const handleSaveSprechen = async () => {
    if (!selectedAttemptId) return;
    setSavingSprechen(true);
    try {
      const params = new URLSearchParams({
        score: sprechenScore,
        maxScore: sprechenMaxScore,
      });
      const res = await fetch(
        `${base}/attempts/${selectedAttemptId}/sprechen-score?${params}`,
        { method: "POST", credentials: "include" }
      );
      if (res.ok) {
        flash("ok", "Sprechen score saved");
        loadAttempts();
      } else {
        flash("err", "Failed to save Sprechen score");
      }
    } finally {
      setSavingSprechen(false);
    }
  };

  const handlePublish = async (attemptId: number) => {
    setPublishing(true);
    try {
      const res = await fetch(`${base}/attempts/${attemptId}/publish`, {
        method: "POST",
        credentials: "include",
      });
      if (res.ok) {
        flash("ok", "Results published");
        loadAttempts();
      } else {
        flash("err", "Failed to publish");
      }
    } finally {
      setPublishing(false);
    }
  };

  const handleUnpublish = async (attemptId: number) => {
    setPublishing(true);
    try {
      const res = await fetch(`${base}/attempts/${attemptId}/unpublish`, {
        method: "POST",
        credentials: "include",
      });
      if (res.ok) {
        flash("ok", "Results unpublished");
        loadAttempts();
      } else {
        flash("err", "Failed to unpublish");
      }
    } finally {
      setPublishing(false);
    }
  };

  const handlePublishAll = async () => {
    if (!window.confirm("Publish results for all submitted attempts?")) return;
    setPublishingAll(true);
    try {
      const res = await fetch(`${base}/assignments/${assignmentId}/publish-all`, {
        method: "POST",
        credentials: "include",
      });
      if (res.ok) {
        const d = await res.json();
        flash("ok", `${d.published} result(s) published`);
        loadAttempts();
      }
    } finally {
      setPublishingAll(false);
    }
  };

  const selectedAttempt = attempts.find((a) => a.id === selectedAttemptId);
  const questionMap = Object.fromEntries(questions.map((q) => [q.id, q]));

  const groupedAnswers = answers.reduce<Record<string, (Answer & { q: Question | undefined })[]>>(
    (acc, ans) => {
      const q = questionMap[ans.questionId];
      const mod = q?.module ?? "UNKNOWN";
      if (!acc[mod]) acc[mod] = [];
      acc[mod].push({ ...ans, q });
      return acc;
    },
    {}
  );

  if (loading) {
    return (
      <div className="flex justify-center py-24">
        <FaSpinner className="animate-spin text-4xl text-[#009fe3]" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Back + header */}
      <div className="flex items-center justify-between">
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 transition-colors"
        >
          <FaArrowLeft size={12} /> Back to Assignments
        </button>

        <button
          onClick={handlePublishAll}
          disabled={publishingAll}
          className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors"
        >
          {publishingAll ? <FaSpinner className="animate-spin" /> : <FaCheckCircle />}
          Publish All
        </button>
      </div>

      {assignment && (
        <div className="bg-white rounded-xl border border-gray-200 px-5 py-4 flex flex-wrap gap-4 items-center">
          <div>
            <div className="font-semibold text-gray-900">{assignment.classTestTitle}</div>
            <div className="flex items-center gap-2 mt-0.5">
              <span className={`px-1.5 py-0.5 rounded-full text-xs font-bold ${LEVEL_COLORS[assignment.level] || "bg-gray-100 text-gray-700"}`}>
                {assignment.level}
              </span>
              <span className="text-xs text-gray-500">{assignment.batchName}</span>
            </div>
          </div>
          <div className="flex gap-6 ml-auto text-sm text-center">
            {[
              { label: "Submitted", val: assignment.submitted },
              { label: "Graded", val: assignment.graded },
              { label: "Published", val: assignment.published },
            ].map(({ label, val }) => (
              <div key={label}>
                <div className="font-bold text-gray-900 text-lg">{val}</div>
                <div className="text-gray-500 text-xs">{label}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {msg && (
        <div
          className={`px-4 py-2 rounded-lg text-sm font-medium ${
            msg.type === "ok" ? "bg-green-50 text-green-700 border border-green-200" : "bg-red-50 text-red-700 border border-red-200"
          }`}
        >
          {msg.text}
        </div>
      )}

      <div className="flex gap-4">
        {/* Student sidebar */}
        <div className="w-56 shrink-0 space-y-1">
          <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide px-2 mb-2 flex items-center gap-1.5">
            <FaUsers size={11} /> Students ({attempts.length})
          </div>
          {attempts.length === 0 ? (
            <div className="text-sm text-gray-400 px-2">No attempts yet.</div>
          ) : (
            attempts.map((a) => (
              <button
                key={a.id}
                onClick={() => setSelectedAttemptId(a.id)}
                className={`w-full text-left px-3 py-2.5 rounded-lg transition-colors ${
                  selectedAttemptId === a.id
                    ? "bg-[#009fe3] text-white"
                    : "bg-white border border-gray-200 text-gray-800 hover:bg-blue-50"
                }`}
              >
                <div className="text-sm font-medium truncate">{a.studentName}</div>
                <div className="flex items-center gap-1.5 mt-0.5">
                  {!a.submittedAt ? (
                    <span className={`text-xs ${selectedAttemptId === a.id ? "text-white/80" : "text-gray-400"}`}>
                      {a.status === "IN_PROGRESS" ? "In progress" : "Not started"}
                    </span>
                  ) : a.resultsPublished ? (
                    <span className={`text-xs font-medium ${selectedAttemptId === a.id ? "text-white/90" : "text-green-600"}`}>
                      ✓ Published {a.passed != null && (a.passed ? "· Pass" : "· Fail")}
                    </span>
                  ) : (
                    <span className={`text-xs ${selectedAttemptId === a.id ? "text-white/80" : "text-orange-600"}`}>
                      Submitted
                      {a.percentage != null && ` · ${Math.round(a.percentage)}%`}
                    </span>
                  )}
                </div>
              </button>
            ))
          )}
        </div>

        {/* Grading panel */}
        <div className="flex-1 min-w-0">
          {!selectedAttempt ? (
            <div className="bg-white rounded-xl border border-gray-200 py-16 text-center text-gray-400">
              Select a student to grade.
            </div>
          ) : (
            <div className="space-y-4">
              {/* Score summary */}
              <div className="bg-white rounded-xl border border-gray-200 px-5 py-4">
                <div className="flex flex-wrap gap-6 items-center justify-between">
                  <div className="flex flex-wrap gap-4 text-sm">
                    {[
                      { label: "Lesen", score: selectedAttempt.lesenScore, max: selectedAttempt.lesenMaxScore },
                      { label: "Hören", score: selectedAttempt.horenScore, max: selectedAttempt.horenMaxScore },
                      { label: "Schreiben", score: selectedAttempt.schreibenScore, max: selectedAttempt.schreibenMaxScore },
                      { label: "Sprechen", score: selectedAttempt.sprechenScore, max: selectedAttempt.sprechenMaxScore },
                    ].map(({ label, score, max }) => (
                      <div key={label} className="text-center">
                        <div className="text-xs text-gray-500">{label}</div>
                        <div className="font-bold text-gray-900">
                          {score ?? "—"}/{max ?? "—"}
                        </div>
                      </div>
                    ))}
                    {selectedAttempt.percentage != null && (
                      <div className="text-center">
                        <div className="text-xs text-gray-500">Total</div>
                        <div className={`font-bold text-lg ${selectedAttempt.passed ? "text-green-600" : "text-red-600"}`}>
                          {Math.round(selectedAttempt.percentage)}%
                          {selectedAttempt.passed != null && (
                            <span className="ml-1 text-xs">{selectedAttempt.passed ? "Pass" : "Fail"}</span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="flex gap-2">
                    {selectedAttempt.resultsPublished ? (
                      <button
                        onClick={() => handleUnpublish(selectedAttempt.id)}
                        disabled={publishing}
                        className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-300 text-gray-700 text-xs font-medium rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors"
                      >
                        {publishing ? <FaSpinner className="animate-spin" size={11} /> : <FaEyeSlash size={11} />}
                        Unpublish
                      </button>
                    ) : (
                      <button
                        onClick={() => handlePublish(selectedAttempt.id)}
                        disabled={publishing || !selectedAttempt.submittedAt}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-green-600 text-white text-xs font-medium rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors"
                      >
                        {publishing ? <FaSpinner className="animate-spin" size={11} /> : <FaEye size={11} />}
                        Publish Results
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* Sprechen score entry */}
              <div className="bg-white rounded-xl border border-gray-200 p-4">
                <div className="flex items-center gap-2 font-semibold text-gray-800 mb-3">
                  <FaMicrophone className="text-[#009fe3]" size={14} />
                  Sprechen Score
                  <span className="text-xs font-normal text-gray-400 bg-yellow-50 border border-yellow-200 text-yellow-700 px-2 py-0.5 rounded ml-1">
                    Admin/Mentor only
                  </span>
                </div>
                {(() => {
                  const sprechenQuestions = questions
                    .filter((q) => q.module === "SPRECHEN")
                    .sort((a, b) => a.questionNumber - b.questionNumber);
                  if (!sprechenQuestions.length) return null;
                  return (
                    <div className="space-y-2 mb-3">
                      {sprechenQuestions.map((q) => (
                        <div key={q.id} className="text-sm bg-orange-50 border border-orange-100 rounded-lg px-3 py-2 text-gray-800">
                          <span className="text-xs text-gray-500 mr-1">Topic {q.questionNumber}:</span>
                          {q.questionText}
                        </div>
                      ))}
                    </div>
                  );
                })()}
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      min={0}
                      value={sprechenScore}
                      onChange={(e) => setSprechenScore(e.target.value)}
                      className="w-20 px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#009fe3]"
                      placeholder="Score"
                    />
                    <span className="text-gray-500 text-sm">/</span>
                    <input
                      type="number"
                      min={1}
                      value={sprechenMaxScore}
                      onChange={(e) => setSprechenMaxScore(e.target.value)}
                      className="w-20 px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#009fe3]"
                      placeholder="Max"
                    />
                  </div>
                  <button
                    onClick={handleSaveSprechen}
                    disabled={savingSprechen || !sprechenScore}
                    className="flex items-center gap-1.5 px-4 py-1.5 bg-[#009fe3] text-white text-sm rounded-lg hover:bg-[#007cb5] disabled:opacity-50 transition-colors"
                  >
                    {savingSprechen ? <FaSpinner className="animate-spin" size={12} /> : <FaSave size={12} />}
                    Save
                  </button>
                </div>
              </div>

              {/* Answers by module */}
              {loadingAnswers ? (
                <div className="flex justify-center py-8">
                  <FaSpinner className="animate-spin text-2xl text-[#009fe3]" />
                </div>
              ) : answers.length === 0 ? (
                <div className="bg-white rounded-xl border border-gray-200 py-8 text-center text-gray-400 text-sm">
                  No answers submitted yet.
                </div>
              ) : (
                ["LESEN", "HOREN", "SCHREIBEN"].map((mod) => {
                  const modAnswers = groupedAnswers[mod];
                  if (!modAnswers?.length) return null;
                  return (
                    <div key={mod} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                      <div className="px-5 py-3 bg-gray-50 border-b border-gray-200 font-semibold text-gray-700 flex items-center gap-2">
                        <span>{MODULE_ICONS[mod]}</span>
                        {mod.charAt(0) + mod.slice(1).toLowerCase()}
                        {mod === "HOREN" && " (Hören)"}
                        {mod === "SCHREIBEN" && selectedAttempt?.submittedAt && (
                          <button
                            onClick={handleEvaluateAI}
                            disabled={evaluatingAI}
                            className="ml-auto flex items-center gap-1.5 px-3 py-1 bg-purple-600 text-white text-xs font-medium rounded-lg hover:bg-purple-700 disabled:opacity-50 transition-colors"
                          >
                            {evaluatingAI ? <FaSpinner className="animate-spin" size={11} /> : <FaRobot size={11} />}
                            AI Evaluate
                          </button>
                        )}
                      </div>
                      <div className="divide-y divide-gray-100">
                        {modAnswers
                          .sort((a, b) => (a.q?.questionNumber ?? 0) - (b.q?.questionNumber ?? 0))
                          .map((ans) => {
                            const q = ans.q;
                            const needsManual = q ? isManual(q.questionType) : false;
                            const gradeInput = gradeInputs[ans.id] ?? { points: "", feedback: "" };

                            return (
                              <div key={ans.id} className="px-5 py-4">
                                <div className="flex items-start justify-between gap-4">
                                  <div className="flex-1 min-w-0">
                                    <div className="text-xs text-gray-500 mb-1">
                                      Q{q?.questionNumber ?? "?"} · {q?.questionType?.replace(/_/g, " ")} · {q?.points ?? "?"} pts
                                    </div>
                                    {q?.contextText && (
                                      <div className="text-xs text-gray-500 bg-gray-50 rounded p-2 mb-2 border border-gray-100 line-clamp-3">
                                        {q.contextText}
                                      </div>
                                    )}
                                    <div className="text-sm text-gray-800 font-medium mb-2">{q?.questionText}</div>

                                    {/* Answer display */}
                                    {(() => {
                                      const bc = q?.blankCount && q.blankCount > 1 ? q.blankCount : null;
                                      const studentParts = parseJsonArray(ans.studentAnswer);
                                      const correctParts = parseJsonArray(q?.correctAnswer ?? null);
                                      if (bc && !needsManual) {
                                        // Multi-answer: show each sub-answer in its own row
                                        return (
                                          <div className="space-y-1 mt-1">
                                            {Array.from({ length: bc }).map((_, idx) => {
                                              const sa = studentParts[idx] ?? null;
                                              const ca = correctParts[idx] ?? null;
                                              const normalize = (v: string | null) => {
                                                if (!v) return "";
                                                const t = v.trim().toLowerCase();
                                                if (["true","richtig","yes","ja","r"].includes(t)) return "true";
                                                if (["false","falsch","no","nein","f"].includes(t)) return "false";
                                                return t;
                                              };
                                              const correct = sa != null && ca != null && normalize(sa) === normalize(ca);
                                              return (
                                                <div key={idx} className="flex items-center gap-2 text-sm">
                                                  <span className="text-xs text-gray-400 w-8 shrink-0">Q{(q!.questionNumber) + idx}</span>
                                                  <span className={`px-2 py-0.5 rounded ${sa ? (correct ? "bg-green-50 text-green-800" : "bg-red-50 text-red-800") : "bg-gray-50 text-gray-400 italic"}`}>
                                                    {sa || "no answer"}
                                                  </span>
                                                  {ca && <span className="text-xs text-gray-400">→ correct: <span className="font-medium text-green-700">{ca}</span></span>}
                                                  {sa && (correct ? <FaCheck size={11} className="text-green-600 shrink-0" /> : <FaTimes size={11} className="text-red-500 shrink-0" />)}
                                                </div>
                                              );
                                            })}
                                          </div>
                                        );
                                      }
                                      // Single answer
                                      return (
                                        <>
                                          <div className="text-sm bg-blue-50 border border-blue-100 rounded-lg px-3 py-2 text-gray-800 min-h-[2rem]">
                                            {ans.studentAnswer || <span className="text-gray-400 italic">No answer</span>}
                                          </div>
                                          {!needsManual && q?.correctAnswer && (
                                            <div className="mt-1.5 text-xs text-gray-500">
                                              Correct: <span className="font-medium text-green-700">{q.correctAnswer}</span>
                                            </div>
                                          )}
                                        </>
                                      );
                                    })()}
                                  </div>

                                  <div className="shrink-0 w-44">
                                    {!needsManual ? (
                                      // Auto-graded
                                      <div className="text-center">
                                        {ans.isCorrect != null ? (
                                          <div className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium ${ans.isCorrect ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>
                                            {ans.isCorrect ? <FaCheck size={12} /> : <FaTimes size={12} />}
                                            {ans.pointsEarned ?? 0}/{ans.maxPoints ?? q?.points ?? 0} pts
                                          </div>
                                        ) : (
                                          <span className="text-xs text-gray-400">Not graded</span>
                                        )}
                                      </div>
                                    ) : (
                                      // Manual grading
                                      <div className="space-y-2">
                                        {/* AI feedback display */}
                                        {ans.gradedBy === "AI" && ans.feedback && (
                                          <div className="mb-2 bg-purple-50 border border-purple-200 rounded-lg p-3 text-xs text-purple-900 space-y-1">
                                            <div className="flex items-center gap-1.5 font-semibold text-purple-700 mb-1">
                                              <FaRobot size={11} /> AI Evaluation
                                            </div>
                                            {[
                                              ["Task Completion", ans.feedback.match(/Task Completion: (\d+)/)?.[1]],
                                              ["Grammar", ans.feedback.match(/Grammar: (\d+)/)?.[1]],
                                              ["Vocabulary", ans.feedback.match(/Vocabulary: (\d+)/)?.[1]],
                                              ["Coherence", ans.feedback.match(/Coherence: (\d+)/)?.[1]],
                                            ].filter(([, v]) => v).map(([label, val]) => (
                                              <div key={label as string} className="flex justify-between">
                                                <span className="text-purple-600">{label}</span>
                                                <span className="font-medium">{val}/10</span>
                                              </div>
                                            ))}
                                          </div>
                                        )}
                                        <div className="flex items-center gap-1.5">
                                          <input
                                            type="number"
                                            min={0}
                                            max={q?.points ?? 100}
                                            value={gradeInput.points}
                                            onChange={(e) =>
                                              setGradeInputs((prev) => ({
                                                ...prev,
                                                [ans.id]: { ...prev[ans.id], points: e.target.value },
                                              }))
                                            }
                                            className="w-16 px-2 py-1 border border-gray-300 rounded text-sm text-center focus:ring-2 focus:ring-[#009fe3]"
                                            placeholder="Pts"
                                          />
                                          <span className="text-xs text-gray-500">/{q?.points ?? "?"}</span>
                                        </div>
                                        <textarea
                                          rows={2}
                                          value={gradeInput.feedback}
                                          onChange={(e) =>
                                            setGradeInputs((prev) => ({
                                              ...prev,
                                              [ans.id]: { ...prev[ans.id], feedback: e.target.value },
                                            }))
                                          }
                                          className="w-full px-2 py-1 border border-gray-300 rounded text-xs focus:ring-2 focus:ring-[#009fe3] resize-none"
                                          placeholder="Feedback…"
                                        />
                                        <button
                                          onClick={() => handleGradeAnswer(ans.id)}
                                          disabled={savingAnswer === ans.id || !gradeInput.points}
                                          className="w-full flex items-center justify-center gap-1.5 px-2 py-1.5 bg-[#009fe3] text-white text-xs font-medium rounded hover:bg-[#007cb5] disabled:opacity-50 transition-colors"
                                        >
                                          {savingAnswer === ans.id ? (
                                            <FaSpinner className="animate-spin" size={10} />
                                          ) : (
                                            <FaSave size={10} />
                                          )}
                                          Save Grade
                                        </button>
                                        {ans.gradedBy && (
                                          <div className="text-xs text-gray-400">
                                            Graded by {ans.gradedBy === "AI" ? "🤖 AI" : ans.gradedBy}
                                          </div>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminClassTestGrade;
